import type { SxProps, Theme } from '@mui/material';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { IconSparkles } from '@tabler/icons-react';

import { insightGradients } from 'src/constants/colors';

interface AIStudioIconButtonProps {
  ariaLabel?: string;
  circleSize?: number;
  isLoading?: boolean;
  onClick: () => void;
  padding?: number;
  sx?: SxProps<Theme>;
}

export const AIStudioIconButton = ({
  ariaLabel,
  circleSize = 26,
  isLoading,
  onClick,
  padding = 0,
  sx,
}: AIStudioIconButtonProps) => {
  const { palette } = useTheme();

  return (
    <Box
      aria-label={ariaLabel}
      component='button'
      disabled={isLoading}
      onClick={onClick}
      sx={{
        '&:hover .trigger-circle::after': {
          opacity: 1,
        },
        '&:hover .trigger-content': {
          color: palette.colors.teal[800],
        },
        alignItems: 'center',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        height: circleSize + padding * 2,
        justifyContent: 'center',
        // String px — a numeric sx `padding` would be run through
        // theme.spacing (8px base), turning `4` into 32px.
        padding: `${padding}px`,
        width: circleSize + padding * 2,
        ...sx,
      }}
      type='button'
    >
      <Box
        className='trigger-circle'
        sx={{
          '&::after': {
            background: insightGradients[100],
            borderRadius: 'inherit',
            content: '""',
            inset: 0,
            opacity: 0,
            position: 'absolute',
            transition: 'opacity 0.7s ease',
          },
          alignItems: 'center',
          background: `linear-gradient(90deg, ${palette.colors.teal[500]} 0%, #6DBBD7 100%)`,
          borderRadius: '100px',
          display: 'flex',
          height: circleSize,
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          width: circleSize,
        }}
      >
        {isLoading ? (
          <CircularProgress
            size={Math.round(circleSize * (18 / 26))}
            sx={{ color: 'white', zIndex: 1 }}
          />
        ) : (
          <IconSparkles
            className='trigger-content'
            color='currentColor'
            size={Math.round(circleSize * (15 / 26))}
            style={{
              color: 'white',
              position: 'relative',
              transition: 'color 0.7s ease',
              zIndex: 1,
            }}
          />
        )}
      </Box>
    </Box>
  );
};
